/**
 * Pause menu — Esc in game. Reference-styled panel over the live game:
 * Resume / Settings / Quit to menu. Pausing freezes the sim (timeScale 0)
 * and restores the previous speed on resume.
 */
import type { SimLoop } from "../sim/loop";

export interface PauseMenuCallbacks {
  onSettings(): void;
  onQuitToMenu(): void;
}

export class PauseMenu {
  private el: HTMLDivElement;
  private sim: SimLoop | null = null;
  private prevScale = 1;
  private open = false;

  constructor(private cb: PauseMenuCallbacks) {
    this.el = document.createElement("div");
    this.el.className = "lg-screen";
    this.el.id = "lg-pause";
    this.el.style.display = "none";
    // Translucent: the battlefield stays visible behind the panel.
    this.el.style.background = "rgba(4,6,10,0.72)";
    this.el.innerHTML =
      `<div class="lg-body">` +
      `<div class="lg-crumb lg-anim">Legionnaire</div>` +
      `<div class="lg-title lg-anim lg-d1" style="font-size:clamp(26px,5.4vw,40px)">PAUSED</div>` +
      `<nav class="lg-nav lg-anim lg-d2" aria-label="Pause menu" style="max-width:min(420px,92vw)">` +
      `<button class="lg-btn primary" data-act="resume">Resume</button>` +
      `<button class="lg-btn" data-act="settings">Settings</button>` +
      `<button class="lg-btn danger" data-act="quit">Quit to menu</button>` +
      `</nav>` +
      `<div class="lg-note lg-anim lg-d3" style="margin-top:18px">Esc — resume</div>` +
      `</div>`;
    this.el
      .querySelector('[data-act="resume"]')!
      .addEventListener("click", () => this.hide());
    this.el
      .querySelector('[data-act="settings"]')!
      .addEventListener("click", () => this.cb.onSettings());
    this.el
      .querySelector('[data-act="quit"]')!
      .addEventListener("click", () => this.cb.onQuitToMenu());
    document.body.appendChild(this.el);
  }

  /** Attach the sim loop whose timeScale is frozen while paused. */
  bind(sim: SimLoop): void {
    this.sim = sim;
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    if (this.sim) {
      this.prevScale = this.sim.timeScale;
      this.sim.timeScale = 0;
    }
    this.el.style.display = "";
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    if (this.sim && this.prevScale !== 0) this.sim.timeScale = this.prevScale;
    this.el.style.display = "none";
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  destroy(): void {
    this.el.remove();
  }
}
